// components/shared/notifications/NotificationForm.tsx
import React from "react";
import { Send } from "lucide-react";
import { NotificationPreview } from "./NotificationPreview";

interface NotificationFormProps {
  title: string;
  message: string;
  notificationType: string;
  priority: string;
  actionUrl?: string;
  actionLabel?: string;
  data?: string;
  onTitleChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  onActionUrlChange: (value: string) => void;
  onActionLabelChange: (value: string) => void;
  onDataChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  sending: boolean;
  isBroadcast: boolean;
}

export const NotificationForm: React.FC<NotificationFormProps> = ({
  title,
  message,
  notificationType,
  priority,
  actionUrl,
  actionLabel,
  data,
  onTitleChange,
  onMessageChange,
  onTypeChange,
  onPriorityChange,
  onActionUrlChange,
  onActionLabelChange,
  onDataChange,
  onSubmit,
  sending,
  isBroadcast,
}) => (
  <div className="bg-white rounded-lg shadow-md p-6">
    <h3 className="mb-4 text-lg font-semibold">{isBroadcast ? "Notification broadcast" : "Nouvelle notification"}</h3>

    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Titre *</label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder="Ex: Nouvelle fonctionnalité disponible"
          required
        />
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Message *</label>
        <textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          rows={4}
          className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder="Décrivez votre notification..."
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Type</label>
          <select
            value={notificationType}
            onChange={(e) => onTypeChange(e.target.value)}
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="SYSTEM">Système</option>
            <option value="ADMIN">Administrateur</option>
            <option value="TRANSACTION">Transaction</option>
            <option value="KYC">KYC</option>
            <option value="PROMOTION">Promotion</option>
            <option value="SECURITY">Sécurité</option>
          </select>
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Priorité</label>
          <select
            value={priority}
            onChange={(e) => onPriorityChange(e.target.value)}
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="LOW">Faible</option>
            <option value="NORMAL">Normale</option>
            <option value="HIGH">Élevée</option>
            <option value="URGENT">Urgente</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">URL d'action (optionnel)</label>
          <input
            type="text"
            value={actionUrl || ""}
            onChange={(e) => onActionUrlChange(e.target.value)}
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="/history"
          />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-gray-700">Label du bouton (optionnel)</label>
          <input
            type="text"
            value={actionLabel || ""}
            onChange={(e) => onActionLabelChange(e.target.value)}
            className="px-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Voir les détails"
          />
        </div>
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Données supplémentaires (JSON, optionnel)
        </label>
        <textarea
          value={data || ""}
          onChange={(e) => onDataChange(e.target.value)}
          rows={2}
          className="px-4 py-2 w-full font-mono text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
          placeholder='{"key": "value"}'
        />
      </div>

      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="mb-3 text-sm font-medium text-gray-700">Aperçu:</p>
        <NotificationPreview
          title={title}
          message={message}
          type={notificationType}
          priority={priority}
          actionLabel={actionLabel}
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        className="flex gap-2 justify-center items-center py-3 w-full font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {sending ? (
          <>
            <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
            Envoi en cours...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {isBroadcast ? "Envoyer à tous les utilisateurs" : "Envoyer la notification"}
          </>
        )}
      </button>
    </form>
  </div>
);
